// The canvas's messages over the meeting's data channel. The sales manager's screen is the source of truth for the
// tour; each customer's screen reports what it is doing, and asks.
//
//   cv   host → all       { scene, variant, spot, zoom, room, yaw, share }     reliable — the tour's state
//   cvp  host → all       { x, y } | null                                     lossy — the pointer, many times a second
//   cva  host → one/all   { ask: "follow" | "tour" }                           reliable — "come and see"
//   cvx  host → one       { id, done: "sent" | "answered" }                    reliable — a request handled
//   cvc  customer → host  { follow, face, scene, variant, room }                reliable — what they are looking at
//   cvr  customer → host  { id, kind: "cost"|"ask"|"book"|"sig", ... }        reliable — requests, and signals
//   cvi  drawer → all     { id, page, tool, pts, end?, cancel? }             lossy — the stroke being drawn (ink.js)
//   cvu  drawer → all     { op: "del", page, ids } | { op: "clear", page }   reliable — undo, erase; clear is the host's
//   cvk  drawer → one/all { page, stroke } | { page, strokes } | { snapshot } a TEXT STREAM — finished ink, whole books
//                         (a data packet caps near 15 KB; a stroke or a snapshot can be larger). A snapshot: host only
//   cvh  host → all       { holders: [identity] }                            reliable — who holds a pen (W2)
//   cvq  anyone → all     { page, x, y } | null                              lossy — her pointer, press and hold (W2)
//   cvf  host → one/all   a BYTE STREAM, "still-<n>-<w>x<h>.jpg"             a frame frozen from his shared tab (W3)
// A "drawer" is the host, or someone in the host's latest cvh. Every screen checks that against LiveKit's stamped identity.
export const TOPIC = { STATE: "cv", PTR: "cvp", ASK: "cva", ACK: "cvx", STATUS: "cvc", REQ: "cvr", INK_LIVE: "cvi", INK_OP: "cvu", INK: "cvk", PEN: "cvh", PEER_PTR: "cvq", STILL: "cvf" };

const enc = new TextEncoder();
const dec = new TextDecoder();

export function makeSender(room) {
  return async function send(topic, body, { reliable = true, to } = {}) {
    try {
      await room.localParticipant.publishData(enc.encode(JSON.stringify(body)), {
        reliable, topic, ...(to ? { destinationIdentities: Array.isArray(to) ? to : [to] } : {}),
      });
      return true;
    } catch {
      return false;
    }
  };
}

// A text stream: reliable and of any size (a finished stroke, a whole book of ink), unlike a data packet.
export function makeTextSender(room) {
  return async function sendText(topic, body, { to } = {}) {
    try {
      await room.localParticipant.sendText(JSON.stringify(body), { topic, ...(to ? { destinationIdentities: Array.isArray(to) ? to : [to] } : {}) });
      return true;
    } catch {
      return false;
    }
  };
}

export function decode(payload) {
  try { return JSON.parse(dec.decode(payload)); } catch { return null; }
}

// Calls fn at most once per `ms`, always delivering the latest arguments.
export function throttle(fn, ms) {
  let last = 0, timer = 0, args = null;
  return (...a) => {
    args = a;
    const wait = ms - (Date.now() - last);
    if (wait <= 0) { last = Date.now(); fn(...args); return; }
    clearTimeout(timer);
    timer = setTimeout(() => { last = Date.now(); fn(...args); }, wait);
  };
}

// A person's role is the first part of their identity ("host-<code>"), which the studio's pass fixes and nobody can
// change. Not the role attribute: everyone may edit their own attributes (a raised hand is one), so a customer could
// otherwise call themself the host.
// "site" is the site camera (Canvas stage 11): someone at the property filming — never a guest, never given a pen.
const ROLES = new Set(["host", "customer", "guest", "rm", "assist", "site"]);
export const roleOf = (p) => { const r = String(p?.identity || "").split("-")[0]; return ROLES.has(r) ? r : "guest"; };
